import { toast } from 'react-toastify';
import Toast from '../components/Toast';
import JSZip from 'jszip';

// Levels are compared trimmed and case-folded: "Secure" and " secure " read as
// the same label to the teacher choosing between them, even though the read-side
// resolver (utils/rubricLevels.js) matches exactly. That asymmetry is deliberate
// — authoring should refuse a pair a human cannot tell apart, while the resolver
// must not change which already-stored selections resolve at all.
const levelKey = (level) =>
  String(level && level.name != null ? level.name : '')
    .trim()
    .toLowerCase();

// Every handle by which a criterion might still be recognised after an edit.
// None is reliable alone: not every rubric stores criteria ids, titles get
// renamed, and indices shift when a criterion is added above. Any ONE matching
// is enough — the consequence of matching too loosely is a warning where a block
// was possible, and only ever for a name this rubric was already saved with.
const criteriaKeys = (criteria, index) => {
  const keys = [`index:${index}`];
  if (criteria?.id != null && criteria.id !== '') keys.push(`id:${criteria.id}`);
  const title = String(criteria?.title || '')
    .trim()
    .toLowerCase();
  if (title) keys.push(`title:${title}`);
  return keys;
};

/**
 * Duplicate level names that are ALREADY SAVED in this rubric, keyed by
 * criterion.
 *
 * Prevention is the point of the duplicate rule, but a hard block on save is
 * enforced against whatever is on screen — including data the teacher did not
 * author and cannot be asked to justify. There are already published rubrics
 * carrying in-criterion duplicates; without this, opening one to fix a typo in
 * an unrelated descriptor would refuse to save until the teacher first renamed
 * levels they may not want to touch. So: an EXISTING duplicate warns, a NEW one
 * blocks.
 */
const preexistingDuplicates = (savedMarkingCriteria) => {
  const found = new Map();
  const criterias = savedMarkingCriteria?.criterias;
  if (!Array.isArray(criterias)) return found;

  criterias.forEach((criteria, index) => {
    const seen = new Set();
    const duplicates = new Set();
    for (const level of criteria?.levels || []) {
      const key = levelKey(level);
      if (!key) continue;
      if (seen.has(key)) duplicates.add(key);
      seen.add(key);
    }
    if (duplicates.size) {
      criteriaKeys(criteria, index).forEach((key) => {
        const existing = found.get(key);
        if (existing) {
          duplicates.forEach((name) => existing.add(name));
        } else {
          found.set(key, new Set(duplicates));
        }
      });
    }
  });
  return found;
};

// The duplicate names this criterion was already saved with, under whichever
// handle still matches.
const toleratedDuplicates = (alreadySaved, criteria, index) => {
  const tolerated = new Set();
  criteriaKeys(criteria, index).forEach((key) => {
    const saved = alreadySaved.get(key);
    if (saved) saved.forEach((name) => tolerated.add(name));
  });
  return tolerated;
};

/**
 * @param savedMarkingCriteria the rubric as last persisted, when this save is an
 *   edit of an existing one. Omitted for a brand-new or imported rubric, where
 *   every duplicate is by definition new and is blocked.
 */
export const validateRubric = (markingCriteria, savedMarkingCriteria) => {
  let isValid = true;
  const alreadySaved = preexistingDuplicates(savedMarkingCriteria);
  if (markingCriteria.title === '' || markingCriteria.title === undefined) {
    toast(<Toast message={'Please enter a title for the rubrics'} />);
    isValid = false;
  }
  if (markingCriteria.criterias.length === 0) {
    toast(<Toast message={'Please add at least one criteria'} />);
    isValid = false;
    return;
  }

  for (
    let indexout = 0;
    indexout < markingCriteria.criterias.length;
    indexout++
  ) {
    const criteria = markingCriteria.criterias[indexout];

    if (criteria.title === undefined || criteria.title === '') {
      toast(
        <Toast message={`Please enter a title for criteria ${indexout + 1}`} />,
      );
      isValid = false;
      return;
    }

    // An award is stored as a level NAME, so two levels sharing one INSIDE a
    // single criteria can never be told apart afterwards. Different criterias
    // reusing a name ("Low" in every column) is normal and stays valid.
    const seenLevelNames = new Set();
    const tolerated = toleratedDuplicates(alreadySaved, criteria, indexout);
    // Three copies of one name would otherwise toast twice about the same name.
    const warned = new Set();
    const criteriaLabel = criteria.title
      ? `"${criteria.title}"`
      : `${indexout + 1}`;

    for (let level of criteria.levels) {
      if (level.name == undefined || level.name === '') {
        toast(
          <Toast
            message={`Please enter a name for all levels in criteria ${
              indexout + 1
            }`}
          />,
        );
        isValid = false;
        return;
      }

      if (level.description == undefined || level.description === '') {
        toast(
          <Toast
            message={`Please enter a description for all levels in criteria ${
              indexout + 1
            }`}
          />,
        );
        isValid = false;
        return;
      }

      const levelName = String(level.name).trim();
      const levelNameKey = levelName.toLowerCase();
      if (seenLevelNames.has(levelNameKey)) {
        if (tolerated.has(levelNameKey)) {
          // Already in the saved rubric — say so, but let the save through.
          // Refusing would strand the teacher on an edit they did not cause.
          if (!warned.has(levelNameKey)) {
            warned.add(levelNameKey);
            toast(
              <Toast
                message={`Criteria ${criteriaLabel} already has more than one level named "${levelName}", so marks awarded there can't tell them apart. Renaming one would fix it.`}
              />,
            );
          }
          continue;
        }
        toast(
          <Toast
            message={`Criteria ${criteriaLabel} has more than one level named "${levelName}". Please give every level in a criteria a different name.`}
          />,
        );
        isValid = false;
        return;
      }
      seenLevelNames.add(levelNameKey);
    }
  }

  return isValid;
};

export function importJsonFile(event, allowedExtensions) {
  return new Promise((resolve, reject) => {
    const file = event.target.files[0];

    // Check if file extension matches any of the allowed extensions
    const fileExtension = file?.name.split('.').pop().toLowerCase();
    const isAllowedExtension = allowedExtensions.includes(fileExtension);

    if (file && isAllowedExtension) {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const jsonData = JSON.parse(e.target.result);
          resolve(jsonData);
        } catch (error) {
          console.error('Error parsing JSON:', error);
          reject(error);
        }
      };

      reader.onerror = (error) => {
        console.error('File reading error:', error);
        reject(error);
      };

      reader.readAsText(file);
    } else {
      toast(
        <Toast
          message={`Please upload a valid file with a .${allowedExtensions.join(' or .')} extension.`}
        />,
      );
      reject(new Error('Invalid file type or file extension'));
    }
  });
}

export const exportJsonFile = (data, title, extension) => {
  const jsonResult = JSON.stringify(data, null, 2);

  const blob = new Blob([jsonResult], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  const sanitizedTitle = title
    ? title
        .replace(/[\/\\:*?"<>|]/g, '')
        .split(' ')
        .join('_')
    : 'Downloaded_File';

  link.href = url;
  link.download = `${sanitizedTitle}.${extension || 'json'}`;
  link.click();

  URL.revokeObjectURL(url);
};

export const exportMultipleFilesAsZip = async (files, zipFilename = 'download') => {
  try {
    const zip = new JSZip();
    const duplicateTracker = {};

    files.forEach(({ data, filename, extension }) => {
      const content = JSON.stringify(data, null, 2);
      const baseName = filename
        ? filename.replace(/[\/\\:*?"<>|]/g, '').split(' ').join('_')
        : 'file';

      const count = duplicateTracker[baseName] ?? 0;
      duplicateTracker[baseName] = count + 1;

      const uniqueName = count === 0 ? baseName : `${baseName}_${count}`;
      const fileExtension = extension || 'json';

      zip.file(`${uniqueName}.${fileExtension}`, content);
    });

    const blob = await zip.generateAsync({ type: 'blob' });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const sanitizedZipFilename = zipFilename
      .replace(/[\/\\:*?"<>|]/g, '')
      .split(' ')
      .join('_');
    
    link.href = url;
    link.download = `${sanitizedZipFilename}.zip`;
    link.click();
    URL.revokeObjectURL(url);

  } catch (error) {
    console.error('Error creating ZIP file:', error);
    toast(<Toast message="Failed to create ZIP file" />);
  }
};
